/**
 * Edit Workout (sc-47391): the add-exercise queue loaded with a saved
 * workout, edited in place and saved with one request
 * (`/actions/exercise/save-workout-edit` → API save-workout-edit).
 *
 * Items keep the queue's shape (see workout-queue-helpers.js) plus:
 *   serverId     saved exercise/section id; absent = added in this modal
 *   original     the saved exercise doc (exercise items)
 *   exercise     the draft save payload; null until the coach changes it
 *   rawForm      form snapshot for the editor; null = build from the draft
 *   removed      marked for deletion (deleted only on save)
 *   locked       already logged by the athlete: read-only, can't move
 *   metcon       edited in the metcon editor, not here
 *   complexIds   all saved members of a complex, moved as one card
 *   rejected     the API refused this card's change (422) on the last save
 */
import { generateQueueItemId } from "./workout-queue-helpers";

// biome-ignore lint/suspicious/noExplicitAny: untyped legacy exercise/set docs
type Doc = Record<string, any>;

export type EditItem = {
  kind: "exercise" | "section";
  id: string;
  serverId?: string;
  name?: string;
  original?: Doc;
  exercise?: Doc | null;
  rawForm?: Doc | null;
  linkedToPrev?: boolean;
  removed?: boolean;
  locked?: boolean;
  metcon?: boolean;
  complexIds?: string[];
  complexMembers?: Doc[];
  rejected?: boolean;
};

export type ItemState = "unchanged" | "edited" | "new" | "removed";

const sid = (value: unknown) => (value == null ? null : String(value));
const orderOf = (doc: Doc) => (typeof doc?.order === "number" ? doc.order : 0);
const complexKey = (doc: Doc) =>
  doc?.complex && doc?.complexInstance
    ? `${doc.complex}_${doc.complexInstance}`
    : null;

/** Same rule as the API's exerciseHasLoggedWork (flexible-helpers.js). */
export const exerciseHasLoggedWork = (exercise: Doc) => {
  if (exercise?.metconResult?.status) return true;
  return (exercise?.sets || []).some((set: Doc) => {
    if (
      Array.isArray(set?.statusArray) &&
      set.statusArray.some((entry: Doc) => entry?.status === "completed")
    )
      return true;
    const outcomes = set?.outcomes;
    if (!outcomes || typeof outcomes !== "object") return false;
    return Object.values(outcomes).some((value) =>
      (Array.isArray(value) ? value : [value]).some((entry: Doc) =>
        Boolean(entry?.status || entry?.outcome)
      )
    );
  });
};

/**
 * The program workout containing `targetId`, as the program builder shows it:
 * the session's own exercise list (not exercises whose `cycle` fields happen
 * to match — stray docs can) and the session's sections. Null when the
 * target isn't in a session of `editCycle`.
 */
export const getProgramWorkout = (
  editCycle: Doc,
  editExercises: Doc[] = [],
  editSections: Doc[] = [],
  targetId: string
) => {
  const target = editExercises.find((doc) => sid(doc._id) === sid(targetId));
  const ref = target?.cycle;
  if (!ref) return null;
  const session = (editCycle?.programs || [])
    .find((program: Doc) => Number(program.order) === Number(ref.program))
    ?.sessions?.find(
      (doc: Doc) =>
        Number(doc.week) === Number(ref.programWeek) &&
        Number(doc.order) === Number(ref.programSession)
    );
  const ids = new Set((session?.exercises || []).map(sid));
  if (!ids.has(sid(targetId))) return null;
  return {
    target,
    exercises: editExercises.filter((doc) => ids.has(sid(doc._id))),
    sections: editSections.filter(
      (doc) =>
        sid(doc.cycle?.id) === sid(ref.id) &&
        Number(doc.cycle?.program) === Number(ref.program) &&
        Number(doc.cycle?.programWeek) === Number(ref.programWeek) &&
        Number(doc.cycle?.programSession) === Number(ref.programSession)
    ),
  };
};

/**
 * Queue items for a saved workout, in display order: sections sort before an
 * exercise with the same order (same as the session table), complex members
 * collapse into one card, and `nextSuperset` pointers become `linkedToPrev`.
 */
export const buildEditItems = (
  exercises: Doc[] = [],
  sections: Doc[] = []
): EditItem[] => {
  const entries = [
    ...exercises.map((doc, seq) => ({ kind: "exercise", doc, seq })),
    ...sections.map((doc, seq) => ({
      kind: "section",
      doc,
      seq: exercises.length + seq,
    })),
  ].sort(
    (a, b) =>
      orderOf(a.doc) - orderOf(b.doc) ||
      (a.kind === b.kind ? 0 : a.kind === "section" ? -1 : 1) ||
      a.seq - b.seq
  );

  const items: EditItem[] = [];
  const complexItems = new Map<string, EditItem>();
  for (const { kind, doc } of entries) {
    if (kind === "section") {
      items.push({
        kind: "section",
        id: generateQueueItemId(),
        serverId: sid(doc._id),
        name: doc.name || "",
      });
      continue;
    }
    const key = complexKey(doc);
    if (key && complexItems.has(key)) {
      const item = complexItems.get(key);
      item.complexIds.push(sid(doc._id));
      item.complexMembers.push(doc);
      item.locked = item.locked || exerciseHasLoggedWork(doc);
      continue;
    }
    const previous = items[items.length - 1];
    const item: EditItem = {
      kind: "exercise",
      id: generateQueueItemId(),
      serverId: sid(doc._id),
      original: doc,
      exercise: null,
      rawForm: null,
      linkedToPrev: Boolean(
        !key &&
          previous?.kind === "exercise" &&
          !previous.complexIds &&
          sid(previous.original?.nextSuperset) === sid(doc._id)
      ),
      locked: exerciseHasLoggedWork(doc),
      metcon: doc.primary === "metcon",
    };
    if (key) {
      item.complexIds = [sid(doc._id)];
      item.complexMembers = [doc];
      complexItems.set(key, item);
    }
    items.push(item);
  }
  return items;
};

/**
 * The editor form for a saved exercise — the same fields the modal's
 * constructor builds for the single-exercise edit it replaces.
 */
export const exerciseToRawForm = (exercise: Doc, exerciseLibrary: Doc = {}) => {
  const { primary, type, secondary } = exercise;
  let secondaryValue: string;
  let secondaryRepMaximum = 0;
  if (type === "primary") {
    secondaryValue = `${primary}`;
  } else {
    secondaryValue = `${secondary}{${primary}}`;
    if (type === "secondary") {
      secondaryRepMaximum =
        exercise.secondaryRepMaximum ||
        (exerciseLibrary[primary] || []).find(
          (item: Doc) => item.movement === secondary
        )?.repMaximum ||
        0;
    }
  }
  return {
    primary,
    secondary: secondaryValue.split("{")[0],
    secondaryValue,
    exerciseMovement: secondary,
    sets: structuredClone(exercise.sets || []),
    type,
    repMaximums: exercise.repMaximums,
    secondaryRepMaximum,
    cycle: exercise.cycle,
    notes: exercise.notes || "",
    repsPerSide: Boolean(exercise.repsPerSide),
    tempo: exercise.tempo || "",
    basicExerciseType: "basicExercise",
    tempoChecked: Boolean(exercise.tempo),
  };
};

/** The editable fields the save sends for an existing exercise. */
const EDITABLE_FIELDS = [
  "primary",
  "type",
  "secondary",
  "secondaryRepMaximum",
  "attributes",
  "repMaximums",
  "sets",
  "notes",
  "repsPerSide",
  "tempo",
  "parameters",
  "video",
  "instructions",
  "exerciseTypeField",
  "primaryMuscleGroup",
  "secondaryMuscleGroup",
  "customName",
  "structure",
];

export const editableFields = (exercise: Doc = {}) => {
  const fields: Doc = {};
  for (const key of EDITABLE_FIELDS) {
    if (key in exercise) fields[key] = structuredClone(exercise[key]);
  }
  return fields;
};

// Derived or athlete-side set keys: never a coach change.
const IGNORED_SET_KEYS = new Set([
  "editMode",
  "units",
  "idx",
  "zone",
  "outcomes",
  "videos",
  "comments",
  "statusArray",
  "_id",
]);

const normalizeValue = (value: unknown) => {
  if (value === null || value === undefined || value === "") return undefined;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "") return undefined;
    const number = Number(trimmed);
    return Number.isNaN(number) ? trimmed : number;
  }
  if (value === false || value === 0) return undefined;
  return value;
};

const normalizeSet = (set: Doc) => {
  const result: Doc = {};
  for (const key of Object.keys(set || {}).sort()) {
    if (IGNORED_SET_KEYS.has(key)) continue;
    // Load follows %1RM when one is set (recomputed from the rep max on save).
    if (key === "load" && set.percentRepMax) continue;
    const value = normalizeValue(set[key]);
    if (value !== undefined) {
      result[key] =
        typeof value === "number" ? Math.round(value * 100) / 100 : value;
    }
  }
  return result;
};

/**
 * What a coach can change about an exercise, normalised so retyping the same
 * value (or "5" vs 5) never reads as an edit.
 */
export const exerciseFingerprint = (exercise: Doc = {}) =>
  JSON.stringify({
    primary: exercise.primary,
    type: exercise.type,
    secondary:
      exercise.type === "primary"
        ? undefined
        : normalizeValue(exercise.secondary),
    notes: normalizeValue(exercise.notes),
    repsPerSide: Boolean(exercise.repsPerSide),
    tempo: normalizeValue(exercise.tempo),
    customName: normalizeValue(exercise.customName),
    sets: (exercise.sets || []).map(normalizeSet),
  });

export const itemState = (item: EditItem): ItemState => {
  if (item.kind !== "exercise") return "unchanged";
  if (item.removed) return "removed";
  if (!item.serverId) return "new";
  if (
    item.exercise &&
    exerciseFingerprint(item.exercise) !== exerciseFingerprint(item.original)
  )
    return "edited";
  return "unchanged";
};

/** Order, sections and supersets of the visible (non-removed) items. */
export const layoutSignature = (items: EditItem[]) =>
  items
    .filter((item) => !item.removed)
    .map((item) =>
      item.kind === "section"
        ? `s:${item.serverId || item.id}:${(item.name || "").trim()}`
        : `e:${item.serverId || item.id}:${item.linkedToPrev ? 1 : 0}`
    )
    .join("|");

export const summarizeChanges = (items: EditItem[], initialLayout: string) => {
  const summary = { edited: 0, added: 0, removed: 0, layoutChanged: false };
  for (const item of items) {
    const state = itemState(item);
    if (state === "edited") summary.edited++;
    else if (state === "new") summary.added++;
    else if (state === "removed" && item.serverId) summary.removed++;
  }
  summary.layoutChanged = layoutSignature(items) !== initialLayout;
  return summary;
};

/** Cards the editor can open (Previous / Next walk these). */
export const isEditableItem = (item: EditItem) =>
  item?.kind === "exercise" && !item.metcon && !item.complexIds;

/* ---------------- Change many at once ---------------- */

export type BulkFields = {
  rpe?: string;
  rest?: string;
  tempo?: string;
  load?: string;
  sets?: string;
  reps?: string;
};

export type BulkErrors = Partial<Record<keyof BulkFields, string>>;

const filled = (value?: string) => value != null && String(value).trim() !== "";

/** m:ss → seconds, or null when not a valid 0:00–10:00 rest. */
export const parseRest = (value: string) => {
  const match = /^(\d{1,2}):([0-5]\d)$/.exec(String(value).trim());
  if (!match) return null;
  const seconds = Number(match[1]) * 60 + Number(match[2]);
  return seconds <= 600 ? seconds : null;
};

/** Error keys (i18n, editWorkout.bulkErrors.*) per invalid filled field. */
export const validateBulkFields = (fields: BulkFields): BulkErrors => {
  const errors: BulkErrors = {};
  if (filled(fields.rpe)) {
    const rpe = Number(fields.rpe);
    if (!(rpe >= 5 && rpe <= 10 && Number.isInteger(rpe * 2)))
      errors.rpe = "rpe";
  }
  if (filled(fields.rest) && parseRest(fields.rest) === null)
    errors.rest = "rest";
  if (filled(fields.tempo) && !/^[0-9X]{4}$/i.test(fields.tempo.trim()))
    errors.tempo = "tempo";
  for (const key of ["sets", "reps"] as const) {
    if (!filled(fields[key])) continue;
    const value = Number(fields[key]);
    if (!(Number.isInteger(value) && value >= 1)) errors[key] = "wholeNumber";
  }
  if (filled(fields.load)) {
    const load = Number(fields.load);
    if (!(load >= 0)) errors.load = "load";
  }
  return errors;
};

const isTimeOrDistanceRow = (set: Doc) =>
  filled(set?.time) || filled(set?.distance);

/** Rep max (kg) a set's %1RM refers to, mirroring AddExerciseSets. */
const repMaxOf = (exercise: Doc) => {
  if (exercise.type === "primary")
    return Number(exercise.repMaximums?.[exercise.primary]) || 0;
  if (exercise.type === "secondary")
    return Number(exercise.secondaryRepMaximum) || 0;
  return 0;
};

/**
 * Write the filled bulk fields into every set row of every editable,
 * non-removed, unlocked exercise in scope. Sets/Reps/RPE/Rest overwrite the
 * row value; Reps skips time/distance rows; Load is a weight in the coach's
 * units (`toKg` converts) and skips load-modifier (drop) rows; a tempo turns
 * Tempo on.
 *
 * @param scope "all", or the item id of a section (that section's cards)
 * @returns new items and how many exercises were changed
 */
export const applyBulkChange = (
  items: EditItem[],
  fields: BulkFields,
  scope: string,
  toKg: (value: number) => number = (value) => value
) => {
  const inScope = new Set<string>();
  let currentSection: string | null = null;
  for (const item of items) {
    if (item.kind === "section") {
      currentSection = item.id;
      continue;
    }
    if (scope === "all" || scope === currentSection) inScope.add(item.id);
  }

  let affected = 0;
  const next = items.map((item) => {
    if (
      !inScope.has(item.id) ||
      !isEditableItem(item) ||
      item.removed ||
      item.locked
    )
      return item;
    affected++;
    const draft = item.exercise
      ? structuredClone(item.exercise)
      : editableFields(item.original);
    const repMax = repMaxOf(draft);
    draft.sets = (draft.sets || []).map((source: Doc) => {
      const set = { ...source };
      if (filled(fields.sets)) set.sets = Number(fields.sets);
      if (filled(fields.reps) && !isTimeOrDistanceRow(set)) {
        set.reps = Number(fields.reps);
        delete set.repsLower;
      }
      if (filled(fields.rpe)) set.rpe = Number(fields.rpe);
      if (filled(fields.rest)) set.restTime = parseRest(fields.rest);
      if (filled(fields.load) && !set.loadDrop) {
        set.load = Math.round(toKg(Number(fields.load)) * 100) / 100;
        set.percentRepMax =
          draft.type !== "assistant" && repMax > 0
            ? Math.round((set.load / repMax) * 10000) / 100
            : 0;
      }
      return set;
    });
    if (filled(fields.tempo)) draft.tempo = fields.tempo.trim().toUpperCase();
    // The editor rebuilds its form from the draft next time the card opens.
    return { ...item, exercise: draft, rawForm: null };
  });
  return { items: next, affected };
};

/* ---------------- Save ---------------- */

/**
 * Body for the save-workout-edit action. Existing exercises send a draft
 * only when edited; removed new exercises are simply dropped.
 */
/** Where a failed save leaves the ids the API rejected, beside the draft. */
export const rejectedIdsKey = (draftKey: string) => `${draftKey}.rejected`;

/**
 * A restored draft against the workout as saved now: each card takes the
 * fresh saved doc and logged state (the athlete may have logged since the
 * draft was made), a card that became logged loses its edits, and the cards
 * the API rejected are tagged.
 */
export const reconcileDraft = (
  draft: EditItem[],
  saved: EditItem[],
  rejectedIds: string[] = []
): EditItem[] => {
  const savedById = new Map(
    saved
      .filter((item) => item.kind === "exercise" && item.serverId)
      .map((item) => [item.serverId, item])
  );
  const rejected = new Set(rejectedIds.map(String));
  return draft.map((item) => {
    const fresh = item.kind === "exercise" && savedById.get(item.serverId);
    if (!fresh) return item;
    const next: EditItem = {
      ...item,
      original: fresh.original,
      complexMembers: fresh.complexMembers,
      locked: fresh.locked,
      rejected: (item.complexIds || [item.serverId]).some((id) =>
        rejected.has(id)
      ),
    };
    if (fresh.locked && !item.locked) {
      next.exercise = null;
      next.rawForm = null;
      next.removed = false;
    }
    return next;
  });
};

export const buildEditSavePayload = (items: EditItem[]) => {
  const payloadItems: Doc[] = [];
  const removed: string[] = [];
  const expectedIds: string[] = [];

  for (const item of items) {
    if (item.kind === "section") {
      if (item.removed) continue;
      payloadItems.push({
        kind: "section",
        ...(item.serverId ? { id: item.serverId } : {}),
        name: (item.name || "").trim(),
      });
      continue;
    }
    const savedIds = item.complexIds || (item.serverId ? [item.serverId] : []);
    expectedIds.push(...savedIds);
    if (item.removed) {
      removed.push(...savedIds);
      continue;
    }
    if (item.complexIds) {
      payloadItems.push({ kind: "complex", ids: item.complexIds });
      continue;
    }
    const state = itemState(item);
    const { movementLibrary, ...exercise } = item.exercise || {};
    payloadItems.push({
      kind: "exercise",
      ...(item.serverId ? { id: item.serverId } : { clientId: item.id }),
      linkedToPrev: Boolean(item.linkedToPrev),
      ...(state === "edited" || state === "new" ? { exercise } : {}),
      ...(movementLibrary && Object.keys(movementLibrary).length
        ? { movementLibrary }
        : {}),
    });
  }

  return { items: payloadItems, removed, expectedIds };
};
